import "../../../shared/styles/list.css";

import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Space,
  Table,
  TablePaginationConfig,
} from "antd";
import {
  ColumnsType,
  FilterValue,
  SorterResult,
} from "antd/lib/table/interface";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { LoadingOutlined } from "@ant-design/icons";

import {
  getProviderEmails,
  getProviderExcel,
  getProviderPaginatedDocuments,
} from "../../../services/providerApi";
import {
  endDateValue,
  startDocumentDateValue,
} from "../../../shared/constants/Resources";
import ProviderDTO from "../../../shared/dtos/providerDTO";
import ContainerLayout from "../../common/containerLayout";
import { InlineForm } from "../../../shared/components/InlineForm";
import { COUNTRIES } from "../../../utils/constants/countries";

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters: Record<string, FilterValue | null>;
}

export interface DataType {
  id: number;
  url: string;
  fileKey: string;
}

const ProviderReportDocumentList = () => {
  const title = "Documentos de conductores";
  const [form] = Form.useForm<any>();
  const [modalForm] = Form.useForm<any>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [emails, setEmails] = useState<ProviderDTO[]>([]);
  const [checkedEmails, setCheckedEmails] = useState<number[]>([]);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [checkAll, setCheckAll] = useState(true);
  const [emailSearch, setEmailSearch] = useState<ProviderDTO[]>([]);

  Form.useWatch("startDate", form);
  Form.useWatch("endDate", form);
  Form.useWatch("search", form);
  Form.useWatch("startDate", modalForm);
  Form.useWatch("endDate", modalForm);

  const columns: ColumnsType<DataType> = [
    {
      title: "Nombre del archivo",
      dataIndex: "fileName",
      sorter: false,
      width: "20%",
    },
    {
      title: "Url",
      dataIndex: "url",
      width: "40%",
      render: (url) => (
        <a href={url} target="_blank" rel="noreferrer">
          {url}
        </a>
      ),
    },
    {
      title: "Nombre del conductor",
      dataIndex: "providerName",
      width: "40%",
    },
  ];

  const [tableParams, setTableParams] = useState<TableParams>({
    filters: {},
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const fetchData = async () => {
    setLoading(true);

    const startDate = form.getFieldValue("startDate");
    const endDate = form.getFieldValue("endDate");
    const startDateModal = modalForm.getFieldValue("startDate");
    const endDateModal = modalForm.getFieldValue("endDate");

    if (!startDateModal && !endDateModal)
      modalForm.setFieldsValue({
        startDate: dayjs(startDocumentDateValue),
        endDate: dayjs(endDateValue),
      });

    if (!startDate && !endDate)
      form.setFieldsValue({
        startDate: dayjs(startDocumentDateValue),
        endDate: dayjs(endDateValue),
      });

    const { data, total } = await getProviderPaginatedDocuments({
      page: tableParams.pagination?.current || 1,
      take: tableParams.pagination?.pageSize || 10,
      ...form.getFieldsValue(true),
    });

    const results: Array<ProviderDTO> = await getProviderEmails(
      modalForm.getFieldsValue(true),
    );

    setData(data);
    setLoading(false);
    setEmails(results);
    setEmailSearch(results);
    setCheckedEmails(results.map((provider: any) => provider.provider_id));
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        total,
      },
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableParams)]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<DataType> | SorterResult<DataType>[],
  ) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  const handleClick = async () => {
    const startDate = form.getFieldValue("startDate");
    const endDate = form.getFieldValue("endDate");
    if (!startDate && !endDate) return;

    setLoading(true);

    const { data, total } = await getProviderPaginatedDocuments({
      page: tableParams.pagination?.current || 1,
      take: tableParams.pagination?.pageSize || 10,
      ...form.getFieldsValue(true),
    });

    setData(data);
    setLoading(false);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        total,
      },
    });
  };

  const handleClickDate = async (values: any) => {
    const { endDate, startDate, type, countryId } = values;
    if (!endDate && !startDate) return;

    setIsModalLoading(true);
    setEmails([]);
    setCheckedEmails([]);

    const results = await getProviderEmails({
      endDate,
      startDate,
      type,
      countryId,
    });

    setIsModalLoading(false);
    setEmails(results);
    setEmailSearch(results);
    setCheckAll(true);
    setCheckedEmails(results.map((provider: any) => provider.provider_id));
  };

  const showModal = () => {
    setIsModalOpen(true);
    setCheckAll(true);
    modalForm.resetFields();
    modalForm.setFieldsValue({
      startDate: dayjs(startDocumentDateValue),
      endDate: dayjs(endDateValue),
    });
    setCheckedEmails(emails.map((provider: any) => provider.provider_id));
  };

  const handleOk = async () => {
    setConfirmLoading(true);
    const key = "generateReport";
    let emailCount = 0;
    try {
      const { startDate, endDate } = modalForm.getFieldsValue(true);
      for (const email of checkedEmails) {
        const excelDocument = await getProviderExcel({
          search: email.toString(),
          startDate,
          endDate,
        });
        const link = document.createElement("a");
        link.href = excelDocument.file;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        emailCount += 1;
        notification.success({
          key,
          message: `${emailCount} de ${checkedEmails.length} reportes se ha generado`,
          description: "espere mientras los reportes se generan",
          placement: "bottom",
        });
      }
      setIsModalOpen(false);
      setCheckedEmails([]);
      setData([]);
      await handleClick();
      setCheckAll(false);
      form.resetFields();
    } catch (e) {
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleCancel = () => {
    setCheckedEmails([]);
    setCheckAll(false);
    setEmailSearch(emails);
    setIsModalOpen(false);
  };

  const handleCheckAll = (e: any) => {
    setCheckAll(e.target.checked);
    setCheckedEmails(
      e.target.checked
        ? emailSearch.map((provider: any) => provider.provider_id)
        : [],
    );
  };

  const onChange = (event: any) => {
    const { value, checked } = event.target;
    if (!checked) {
      let emailsClone = [...checkedEmails];
      emailsClone = emailsClone.filter((x) => x !== value);
      setCheckedEmails(emailsClone);
      setCheckAll(false);
      return;
    }
    setCheckedEmails([...checkedEmails, value]);
  };

  const filterCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().trim();

    if (!value) {
      setEmailSearch(emails);
      return;
    }

    const result = emails.filter((provider) => {
      const firstName = provider.provider_first_name?.toLowerCase() || "";
      const lastName = provider.provider_last_name?.toLowerCase() || "";

      return firstName.includes(value) || lastName.includes(value);
    });

    setEmailSearch(result);
  };

  return (
    <ContainerLayout title={title} showNotification={false}>
      <Col span={24}>
        <Form form={form} onFinish={handleClick}>
          <Space>
            <Form.Item name="startDate">
              <DatePicker />
            </Form.Item>
            <Form.Item name="endDate">
              <DatePicker />
            </Form.Item>
            <Form.Item name="search">
              <Input type="text" placeholder="Buscar por email" />
            </Form.Item>
            <Form.Item>
              <Button htmlType="submit" disabled={loading === true}>
                Filtrar
              </Button>
            </Form.Item>
            <Form.Item>
              <Button onClick={showModal} disabled={loading === true}>
                Generar reporte
              </Button>
            </Form.Item>
          </Space>
        </Form>
      </Col>
      <Col span={24} style={{ width: "100px", height: "83vh" }}>
        <Table
          style={{ minWidth: "100%" }}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ y: "calc(-250px + 97vh)" }}
        />
      </Col>
      <Modal
        title="Selecciona los conductores para generar los reportes"
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={confirmLoading}
        okText={confirmLoading ? "Descargando" : "Ok"}
      >
        <Row gutter={[2, 16]}>
          <Form
            form={modalForm}
            onFinish={handleClickDate}
            layout="horizontal"
            colon={false}
          >
            <InlineForm>
              <Form.Item name="startDate" label="Fecha de inicio">
                <DatePicker />
              </Form.Item>
              <Form.Item name="endDate" label="Fecha de fin">
                <DatePicker />
              </Form.Item>
            </InlineForm>
            <InlineForm>
              <Form.Item name="countryId" label="País" initialValue={-1}>
                <Select
                  showSearch
                  placeholder="Selecciona un país"
                  optionFilterProp="label"
                  options={COUNTRIES}
                />
              </Form.Item>
            </InlineForm>
            <InlineForm>
              <Form.Item
                name="type"
                label="Tipo de conductor"
                initialValue={"all"}
              >
                <Select
                  placeholder="Selecione un tipo de conductor"
                  options={[
                    { value: "all", label: "Todos" },
                    { value: "temporal", label: "Temporales" },
                    { value: "permanent", label: "Permanentes" },
                  ]}
                ></Select>
              </Form.Item>
            </InlineForm>
            <InlineForm>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={loading === true}
                >
                  Filtrar
                </Button>
              </Form.Item>
              <Form.Item label="Seleccionar todo">
                <Checkbox
                  checked={checkAll}
                  onChange={handleCheckAll}
                ></Checkbox>
              </Form.Item>
            </InlineForm>
            <Form.Item name="search">
              <Input placeholder="Ingresar nombre" onChange={filterCheckbox} />
            </Form.Item>
          </Form>
          <Col
            span={24}
            style={{
              maxHeight: "30vh",
              overflowX: "hidden",
              overflowY: "scroll",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isModalLoading && (
              <Col className="loading-container error-container" span={24}>
                <LoadingOutlined />
              </Col>
            )}
            {!isModalLoading &&
              emailSearch.map((x) => (
                <Checkbox
                  key={x.provider_id}
                  value={x.provider_id}
                  onChange={onChange}
                  checked={checkedEmails?.includes(x.provider_id!)}
                >
                  {x.provider_first_name} {x.provider_last_name}
                  {Number(x.provider_is_temporal) === 1 ? " (Temporal)" : ""}
                </Checkbox>
              ))}
          </Col>
        </Row>
      </Modal>
    </ContainerLayout>
  );
};

export default ProviderReportDocumentList;
